import React, { FC, useMemo } from 'react';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { TokensPair } from '@app/components/TokensPair';
import { useMedia } from 'react-use';
import { usePendingRewardInfo } from '@app/hooks/ryo/usePendingRewardInfo';
import { usePairDetails } from '@app/hooks/pair/usePairDetails';
import { useTokenBalance } from '@app/hooks/swap/useTokenBalance';
import { TokenIcon } from '@app/components/TokenIcon';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { TransactionError } from '@thirdweb-dev/react';
import { formatNumber, toOptionalFixed } from '@app/helpers/format';
import { useTokenPriceV4 } from '@app/hooks/token/useTokenPriceV4';
import { formatUSD } from '@app/helpers/formatUSD';
import { useVaults } from '@app/hooks/plugins/useVaults';

interface Props {
  index: number;
}

export const PendingRewardRow: FC<Props> = ({ index }) => {
  const isMobile = useMedia('(max-width: 960px)', false);

  const { pendingDividendsAmount, claim, distributedToken } =
    usePendingRewardInfo(index);
  const { data: pairData } = usePairDetails({
    id: distributedToken?.toLowerCase()
  });
  const { data: tokenData } = useTokenBalance(distributedToken);

  const usdAmount = useTokenPriceV4(distributedToken, pendingDividendsAmount);

  const { data: vaults } = useVaults();

  const data = useMemo(() => {
    if (!distributedToken) {
      return null;
    }

    const vaultData = vaults?.find(
      v => v.lpToken.id.toLowerCase() === distributedToken.toLowerCase()
    );

    if (vaultData) {
      return {
        token0: vaultData.token0,
        token1: vaultData.token1,
        usdAmount:
          pendingDividendsAmount !== undefined
            ? vaultData.lpToken.lpTokenUSD * +pendingDividendsAmount
            : 0
      };
    }

    if (pairData) {
      return {
        token0: pairData.token0,
        token1: pairData.token1,
        usdAmount
      };
    }

    if (tokenData) {
      return {
        token0: tokenData,
        token1: undefined,
        usdAmount
      };
    }

    return null;
  }, [
    distributedToken,
    vaults,
    pairData,
    tokenData,
    pendingDividendsAmount,
    usdAmount
  ]);

  if (!data || pendingDividendsAmount === 0) {
    return null;
  }

  function renderSubmitButton() {
    return (
      <Button
        variant="row"
        width={['100%', 'fit-content']}
        isDisabled={!pendingDividendsAmount}
        onClick={async () => {
          try {
            const res = await claim();

            const txHash = res?.receipt?.transactionHash;

            if (txHash) {
              showSuccessToast(
                `Successfully claimed reward`,
                <ExternalLink txHash={txHash} />
              );
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Error claiming reward`);
            }
          }
        }}
      >
        Claim
      </Button>
    );
  }

  if (isMobile) {
    return (
      <VStack
        mb="24px"
        alignItems="flex-start"
        sx={{
          borderRadius: '12px',
          '&:nth-of-type(even)': {
            backgroundColor: 'var(--chakra-colors-neutral-1000)'
          }
        }}
      >
        <HStack sx={{ width: '100%' }}>
          {data.token1 !== undefined ? (
            <TokensPair
              token0Symbol={data.token0.symbol}
              token1Symbol={data.token1.symbol}
            />
          ) : (
            <TokenIcon width="32px" height="32px" symbol={data.token0.symbol} />
          )}
          <Text
            fontSize="18px"
            fontWeight={400}
            lineHeight="26px"
            color="white"
            marginLeft="auto"
          >
            {pendingDividendsAmount !== undefined
              ? formatNumber(pendingDividendsAmount, 3)
              : '0'}{' '}
            {data.token0.symbol}
            {data.token1 ? `- ${data.token1.symbol}` : ''}
          </Text>
          <Text
            fontSize="18px"
            fontWeight={400}
            lineHeight="26px"
            color="gray.4"
          >
            ({data.usdAmount ? formatUSD.format(data.usdAmount) : '$0.00'})
          </Text>
        </HStack>
        <HStack sx={{ width: '100%', mt: '12px' }}>
          {renderSubmitButton()}
        </HStack>
      </VStack>
    );
  }

  return (
    <HStack mb="32px">
      <HStack sx={{ width: '80%' }}>
        {data.token1 !== undefined ? (
          <TokensPair
            token0Symbol={data.token0.symbol}
            token1Symbol={data.token1.symbol}
          />
        ) : (
          <TokenIcon width="32px" height="32px" symbol={data.token0.symbol} />
        )}
        <Text fontSize="18px" fontWeight={400} lineHeight="26px" color="white">
          {pendingDividendsAmount !== undefined
            ? toOptionalFixed(pendingDividendsAmount, 3)
            : '0'}{' '}
          {data.token0.symbol}
          {data.token1 ? `- ${data.token1.symbol}` : ''}
        </Text>
        <Text fontSize="18px" fontWeight={400} lineHeight="26px" color="gray.4">
          ({data.usdAmount ? formatUSD.format(data.usdAmount) : '$0.00'})
        </Text>
      </HStack>
      <HStack justifyContent="flex-end" sx={{ width: '20%' }}>
        {renderSubmitButton()}
      </HStack>
    </HStack>
  );
};
